import { Box, Button, Divider, Flex, Heading, IconButton, VStack } from "@chakra-ui/react";
import { ReactElement, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuColumns2 } from "react-icons/lu";
import useIsMobile from "../../hooks/useIsMobile";
import { UserRole } from "../../api/types";
import useAuthStore from "../../store/auth";

export interface MenuItem<T = string> {
  item: T;
  icon: ReactElement;
  labelKey: string;
  roles?: UserRole[];
}

type SidebarMenuProps<T = string> = {
  titleKey: string;
  menuItems: MenuItem<T>[];
  onSelect?: (item: T) => void;
};

const SidebarMenu = <T,>({ titleKey, menuItems, onSelect }: SidebarMenuProps<T>): ReactElement => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(isMobile);
  const [user] = useAuthStore(state => [state.user]);

  const availableMenuItems = menuItems.filter(
    item => !item.roles || (user?.role && item.roles.includes(user.role)),
  );
  const [selectedItem, setSelectedItem] = useState<T>();

  const handleSelect = useCallback(
    (item: T) => {
      setSelectedItem(item);
      if (onSelect) {
        onSelect(item);
      }

      if (isMobile) {
        setIsMenuOpen(false);
      }
    },
    [onSelect, isMobile],
  );

  useEffect(() => {
    setIsMenuOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    handleSelect(availableMenuItems[0]?.item);
  }, []);

  return (
    <Box
      bg="gray.100"
      p={isMobile ? 2 : 4}
      // eslint-disable-next-line no-nested-ternary
      width={isMenuOpen ? "220px" : isMobile ? "55px" : "70px"}
      transition="width 0.3s ease"
    >
      <VStack align="stretch" spacing={3}>
        <Flex justify="space-between" align="center">
          <Heading
            size="md"
            opacity={isMenuOpen ? 1 : 0}
            maxW={isMenuOpen ? "200px" : "0px"}
            overflow="hidden"
            whiteSpace="nowrap"
            transition="all 0.3s ease"
          >
            {t(titleKey)}
          </Heading>

          <IconButton
            icon={<LuColumns2 />}
            aria-label="open/close menu"
            variant="ghost"
            colorScheme="blue"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          />
        </Flex>

        <Divider borderColor="#333" hidden={isMenuOpen} />

        {availableMenuItems.map(({ item, icon, labelKey }) =>
          isMenuOpen ? (
            <Button
              key={String(item)}
              variant={selectedItem === item ? "solid" : "ghost"}
              colorScheme="blue"
              onClick={() => handleSelect(item)}
              justifyContent="flex-start"
              leftIcon={icon}
              whiteSpace="normal"
              textAlign="left"
            >
              {t(labelKey)}
            </Button>
          ) : (
            <IconButton
              key={String(item)}
              variant={selectedItem === item ? "solid" : "ghost"}
              colorScheme="blue"
              onClick={() => handleSelect(item)}
              icon={icon}
              aria-label={t(labelKey)}
            />
          ),
        )}
      </VStack>
    </Box>
  );
};

export default SidebarMenu;
